import React, { useMemo, useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import { useAgent } from "@/lib/agentContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";

const fmt = (n) => "₹ " + (n || 0).toLocaleString("en-IN");

export default function Financials() {
  const agent = useAgent();
  const [propertyValue, setPropertyValue] = useState(8000000);
  const [gender, setGender] = useState("male");
  const [downpaymentPct, setDownpaymentPct] = useState([20]);
  const [tenure, setTenure] = useState([20]);
  const [interest, setInterest] = useState([7.25]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const compute = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/calculator/compute", {
        property_value: Number(propertyValue),
        buyer_gender: gender,
        downpayment_pct: downpaymentPct[0],
        tenure_years: tenure[0],
        interest_rate: interest[0],
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calculation failed.");
    } finally { setLoading(false); }
  };

  const chartData = useMemo(() => result ? [
    { name: "Property", value: result.inputs.property_value, color: "#0B1B33" },
    { name: "Stamp Duty", value: result.stamp_duty, color: "#C9A24A" },
    { name: "Registration", value: result.registration, color: "#A98532" },
  ] : [], [result]);

  return (
    (() => {
      const inner = (
        <>
          <section className="bg-sg-navy text-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
              <div className="overline text-sg-gold mb-5">MAHARASHTRA · STAMP DUTY + EMI</div>
              <h1 className="font-display text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
                Real Pune <span className="font-serif-italic text-sg-gold">acquisition math.</span> No surprises at the registrar.
              </h1>
              <p className="mt-6 text-white/75 max-w-2xl">
                {agent
                  ? `${agent.name.split(" ")[0]} uses the same engine for every client deal: Stamp Duty (Male 7% · Female 6% · Joint 6.5%) + Registration (≤ ₹30L = 1% · else flat ₹30,000) + EMI amortisation.`
                  : "Stamp duty (Male 7% · Female 6% · Joint 6.5%) + Registration (≤ ₹30L = 1% · else flat ₹30,000) + EMI amortisation."}
              </p>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-sg-line rounded-3xl p-8 space-y-6">
          <div className="space-y-2">
            <Label className="overline text-sg-gold-deep">Property Value (₹)</Label>
            <Input type="number" value={propertyValue} onChange={(e) => setPropertyValue(e.target.value)} className="font-mono text-lg rounded-full border-sg-line" data-testid="public-calc-value" />
            <p className="text-xs text-sg-mute font-mono">{fmt(Number(propertyValue))}</p>
          </div>
          <div className="space-y-2">
            <Label className="overline text-sg-gold-deep">Buyer Gender</Label>
            <Select value={gender} onValueChange={setGender}>
              <SelectTrigger className="rounded-full border-sg-line" data-testid="public-calc-gender"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male (7%)</SelectItem>
                <SelectItem value="female">Female (6%)</SelectItem>
                <SelectItem value="joint">Joint (6.5%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <SliderField label="Downpayment" value={downpaymentPct} onChange={setDownpaymentPct} min={0} max={100} step={5} unit="%" testid="public-calc-dp" />
          <SliderField label="Tenure" value={tenure} onChange={setTenure} min={1} max={30} step={1} unit=" years" testid="public-calc-tenure" />
          <SliderField label="Interest Rate" value={interest} onChange={setInterest} min={5} max={15} step={0.05} unit="%" testid="public-calc-rate" />
          <Button onClick={compute} disabled={loading} className="w-full h-12 rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white" data-testid="public-calc-submit">
            {loading ? "Computing…" : "Calculate Acquisition Cost"}
          </Button>
        </div>

        <div className="bg-white border border-sg-line rounded-3xl p-8">
          {!result ? (
            <div className="h-full flex items-center justify-center text-sg-mute text-sm py-16 text-center">
              Enter values and tap calculate. We'll break down stamp duty, registration and EMI.
            </div>
          ) : (
            <div data-testid="public-calc-result">
              <div className="overline text-sg-gold-deep">TOTAL ACQUISITION COST</div>
              <div className="rupee text-5xl text-sg-navy mt-2">{fmt(result.total_acquisition)}</div>
              <div className="h-44 my-4">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={chartData} dataKey="value" innerRadius={50} outerRadius={80} paddingAngle={2}>
                      {chartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <Row k={`Stamp Duty (${result.stamp_duty_pct}%)`} v={fmt(result.stamp_duty)} accent />
              <Row k="Registration" v={fmt(result.registration)} />
              <Row k="Downpayment" v={fmt(result.downpayment)} />
              <Row k="Loan Amount" v={fmt(result.loan_amount)} />
              <div className="mt-6 bg-sg-cream-soft rounded-2xl border border-sg-line p-5">
                <div className="overline text-sg-gold-deep">MONTHLY EMI</div>
                <div className="rupee text-3xl text-sg-navy mt-1">{fmt(result.emi_monthly)}</div>
                <div className="text-xs text-sg-mute mt-1 font-mono">{result.tenure_months} months · Total interest {fmt(result.total_interest)}</div>
              </div>
            </div>
          )}
        </div>
      </section>
        </>
      );
      return agent ? inner : <PublicLayout>{inner}</PublicLayout>;
    })()
  );
}

function SliderField({ label, value, onChange, min, max, step, unit, testid }) {
  return (
    <div className="space-y-2">
      <div className="flex justify-between">
        <Label className="overline text-sg-gold-deep">{label}</Label>
        <span className="font-mono text-sm font-semibold text-sg-navy">{value[0]}{unit}</span>
      </div>
      <Slider value={value} onValueChange={onChange} min={min} max={max} step={step} data-testid={testid} />
    </div>
  );
}

function Row({ k, v, accent }) {
  return (
    <div className={`flex justify-between border-b border-dashed border-sg-line py-2 ${accent ? "text-sg-gold-deep" : "text-sg-navy"}`}>
      <span className="text-sm">{k}</span>
      <span className="font-mono text-sm font-semibold">{v}</span>
    </div>
  );
}
